import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Add event listeners for megamenu sections to keep them open on hover
    this.#setupMegamenuSectionListeners();

    onDocumentLoaded(this.#preloadImages);
  }

  /**
   * Set up event listeners for megamenu sections
   */
  #setupMegamenuSectionListeners = () => {
    const megamenuSections = document.querySelectorAll('[data-megamenu-handle]');
    megamenuSections.forEach((section) => {
      section.addEventListener('pointerenter', () => {
        this.#debouncedDeactivate.cancel();
      }, { signal: this.#abortController.signal });

      section.addEventListener('pointerleave', () => {
        this.#debouncedDeactivate();
      }, { signal: this.#abortController.signal });
    });
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      // Hide any previously active megamenu section
      this.#hideMegamenuSection(previouslyActiveItem);
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Check for custom megamenu section matching this menu item's handle
    const menuHandle = this.#getMenuHandle(item);
    const megamenuSection = menuHandle ? this.#findMegamenuSection(menuHandle) : null;

    if (megamenuSection) {
      // Show the custom megamenu section
      this.#showMegamenuSection(megamenuSection);
      const sectionHeight = megamenuSection.offsetHeight;
      this.style.setProperty('--submenu-height', `${sectionHeight}px`);
      this.style.setProperty('--submenu-opacity', '1');
    } else {
      // Fall back to default submenu behavior
      let submenu = findSubmenu(item);
      let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

      if (!submenu && !isDefaultSlot) {
        submenu = this.overflowMenu;
      }

      const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

      this.style.setProperty('--submenu-height', `${submenuHeight}px`);
      this.style.setProperty('--submenu-opacity', '1');
    }
  };

  /**
   * Get the menu handle from a menu item
   * @param {HTMLElement} item
   * @returns {string | null}
   */
  #getMenuHandle = (item) => {
    const listItem = item.closest('[data-menu-handle]');
    return listItem?.dataset.menuHandle || null;
  };

  /**
   * Find a megamenu section by handle
   * @param {string} handle
   * @returns {HTMLElement | null}
   */
  #findMegamenuSection = (handle) => {
    return document.querySelector(`[data-megamenu-handle="${handle}"]`);
  };

  /**
   * Show a megamenu section
   * @param {HTMLElement} section
   */
  #showMegamenuSection = (section) => {
    section.style.display = 'block';
    section.style.opacity = '1';
    section.style.pointerEvents = 'auto';
  };

  /**
   * Hide a megamenu section for a menu item
   * @param {HTMLElement} item
   */
  #hideMegamenuSection = (item) => {
    const menuHandle = this.#getMenuHandle(item);
    if (menuHandle) {
      const section = this.#findMegamenuSection(menuHandle);
      if (section) {
        section.style.display = 'none';
        section.style.opacity = '0';
        section.style.pointerEvents = 'none';
      }
    }
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    // Hide any active megamenu section
    this.#hideMegamenuSection(item);

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
